import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Plus, Loader2, MapPin, UsersRound, BookOpen,
  Trash2, LogOut, Navigation, Calendar, Coffee, Check, X, HelpCircle,
  MessageSquare, Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BookClubs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [allClubs, setAllClubs] = useState([]);
  const [myClubs, setMyClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detectingLoc, setDetectingLoc] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', genre: '', city: '', lat: '', lng: '', current_book: ''
  });

  // Meetings state
  const [meetingsDialogOpen, setMeetingsDialogOpen] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [meetingFormOpen, setMeetingFormOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: '', description: '', meeting_date: '', venue_name: '', venue_address: ''
  });
  const [creatingMeeting, setCreatingMeeting] = useState(false);

  // Discussion threads state
  const [discussionsDialogOpen, setDiscussionsDialogOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async () => {
    try {
      const [allRes, mineRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bookclubs`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/mine`, { withCredentials: true })
      ]);
      setAllClubs(allRes.data);
      setMyClubs(mineRes.data);
    } catch (error) {
      toast.error('Failed to load book clubs');
    } finally {
      setLoading(false);
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }
    setDetectingLoc(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/geocode/reverse`,
            { params: { lat: latitude, lng: longitude }, withCredentials: true }
          );
          setForm(prev => ({ ...prev, city: data.city, lat: latitude.toFixed(6), lng: longitude.toFixed(6) }));
          toast.success(`Location: ${data.city}`);
        } catch {
          setForm(prev => ({ ...prev, lat: latitude.toFixed(6), lng: longitude.toFixed(6) }));
        } finally {
          setDetectingLoc(false);
        }
      },
      () => { setDetectingLoc(false); toast.error('Location denied'); }
    );
  };

  const createClub = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs`,
        {
          name: form.name,
          description: form.description,
          genre: form.genre,
          city: form.city || null,
          lat: form.lat ? parseFloat(form.lat) : null,
          lng: form.lng ? parseFloat(form.lng) : null,
          current_book: form.current_book || null
        },
        { withCredentials: true }
      );
      toast.success('Book club created!');
      setForm({ name: '', description: '', genre: '', city: '', lat: '', lng: '', current_book: '' });
      setDialogOpen(false);
      fetchClubs();
    } catch (error) {
      toast.error('Failed to create club');
    } finally {
      setCreating(false);
    }
  };

  const joinClub = async (clubId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${clubId}/join`,
        {}, { withCredentials: true }
      );
      toast.success('Joined book club!');
      fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const leaveClub = async (clubId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${clubId}/leave`,
        {}, { withCredentials: true }
      );
      toast.success('Left the club');
      fetchClubs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const deleteClub = async (clubId) => {
    if (!window.confirm('Delete this book club? This cannot be undone.')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${clubId}`,
        { withCredentials: true }
      );
      toast.success('Club deleted');
      fetchClubs();
    } catch (error) {
      toast.error('Failed to delete club');
    }
  };

  // Meeting functions
  const openMeetings = async (club) => {
    setSelectedClub(club);
    setMeetingsDialogOpen(true);
    setMeetingFormOpen(false);
    await fetchMeetings(club.id);
  };

  const fetchMeetings = async (clubId) => {
    setMeetingsLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${clubId}/meetings`,
        { withCredentials: true }
      );
      setMeetings(data);
    } catch (error) {
      toast.error('Failed to load meetings');
    } finally {
      setMeetingsLoading(false);
    }
  };

  const createMeeting = async (e) => {
    e.preventDefault();
    if (!selectedClub) return;
    setCreatingMeeting(true);
    try {
      const isoDate = new Date(meetingForm.meeting_date).toISOString();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${selectedClub.id}/meetings`,
        {
          title: meetingForm.title,
          description: meetingForm.description || null,
          meeting_date: isoDate,
          venue_name: meetingForm.venue_name,
          venue_address: meetingForm.venue_address || null
        },
        { withCredentials: true }
      );
      toast.success('Meeting scheduled!');
      setMeetingForm({ title: '', description: '', meeting_date: '', venue_name: '', venue_address: '' });
      setMeetingFormOpen(false);
      fetchMeetings(selectedClub.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setCreatingMeeting(false);
    }
  };

  const rsvpMeeting = async (meetingId, status) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/meetings/${meetingId}/rsvp`,
        { status },
        { withCredentials: true }
      );
      toast.success(status === 'going' ? "You're going!" : status === 'maybe' ? 'Marked as maybe' : "Can't make it");
      fetchMeetings(selectedClub.id);
    } catch (error) {
      toast.error('Failed to RSVP');
    }
  };

  const deleteMeeting = async (meetingId) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/meetings/${meetingId}`,
        { withCredentials: true }
      );
      toast.success('Meeting deleted');
      fetchMeetings(selectedClub.id);
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  // Discussion functions
  const openDiscussions = async (club) => {
    setSelectedClub(club);
    setDiscussionsDialogOpen(true);
    await fetchPosts(club.id);
  };

  const fetchPosts = async (clubId) => {
    setPostsLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${clubId}/posts`,
        { withCredentials: true }
      );
      setPosts(data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setPostsLoading(false);
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim() || !selectedClub) return;
    setPosting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookclubs/${selectedClub.id}/posts`,
        { content: newPostContent },
        { withCredentials: true }
      );
      toast.success('Posted!');
      setNewPostContent('');
      fetchPosts(selectedClub.id);
    } catch (error) {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/posts/${postId}`,
        { withCredentials: true }
      );
      toast.success('Post deleted');
      fetchPosts(selectedClub.id);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const addComment = async (postId) => {
    const content = commentInputs[postId]?.trim();
    if (!content) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/posts/${postId}/comments`,
        { content },
        { withCredentials: true }
      );
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      fetchPosts(selectedClub.id);
    } catch (error) {
      toast.error('Failed to comment');
    }
  };

  const deleteComment = async (postId, commentId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/posts/${postId}/comments/${commentId}`,
        { withCredentials: true }
      );
      fetchPosts(selectedClub.id);
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const ClubCard = ({ club, showLeave = false }) => (
    <div className="glass-card p-6 rounded-2xl card-hover" data-testid="bookclub-card">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-bold text-xl text-[#2D2A26]">{club.name}</h3>
        <span className="genre-badge">{club.genre}</span>
      </div>
      <p className="text-sm text-[#5C5955] mb-3 line-clamp-2">{club.description}</p>
      {club.current_book && (
        <div className="flex items-center gap-2 text-sm text-[#2D2A26] mb-3 bg-[#E2E6D5]/30 p-2 rounded-lg">
          <BookOpen className="w-4 h-4 text-[#6B7A4F]" />
          <span>Currently reading: <span className="font-semibold">{club.current_book}</span></span>
        </div>
      )}
      <div className="flex items-center justify-between text-sm text-[#5C5955] mb-4">
        <div className="flex items-center gap-3">
          {club.location?.city && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {club.location.city}
            </span>
          )}
          <span className="flex items-center gap-1">
            <UsersRound className="w-3 h-3" />
            {club.member_count} member{club.member_count !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <div className="text-xs text-[#5C5955] mb-3">Created by {club.creator_name}</div>
      {showLeave ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => openMeetings(club)}
              className="bg-[#8E9E82] hover:bg-[#7A8A72] text-white rounded-full"
              data-testid="bookclub-meetings-btn"
            >
              <Calendar className="w-4 h-4 mr-1" />
              Meetings
            </Button>
            <Button
              onClick={() => openDiscussions(club)}
              className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
              data-testid="bookclub-discussions-btn"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Discuss
            </Button>
          </div>
          {club.creator_id === user._id ? (
            <Button
              onClick={() => deleteClub(club.id)}
              variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-full"
              data-testid="bookclub-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Club
            </Button>
          ) : (
            <Button
              onClick={() => leaveClub(club.id)}
              variant="outline"
              className="w-full border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white rounded-full"
              data-testid="bookclub-leave-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Leave Club
            </Button>
          )}
        </div>
      ) : club.is_member ? (
        <Button
          disabled
          className="w-full bg-[#8E9E82] text-white rounded-full opacity-75"
        >
          Already a Member
        </Button>
      ) : (
        <Button
          onClick={() => joinClub(club.id)}
          className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
          data-testid="bookclub-join-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Join Club
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7] paper-texture">
      <div className="container mx-auto px-6 py-12">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 text-[#5C5955] hover:text-[#6B7A4F]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div data-testid="bookclubs-container" className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-light text-[#2D2A26]">
                Book <span className="font-bold">Clubs</span>
              </h1>
              <p className="text-[#5C5955] mt-2">Join local reading groups or start your own</p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                  data-testid="bookclub-create-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Club
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create a Book Club</DialogTitle>
                  <DialogDescription className="sr-only">Manage details here</DialogDescription>
                </DialogHeader>
                <form onSubmit={createClub} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="club-name">Club Name *</Label>
                    <Input
                      id="club-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      placeholder="Cozy Mystery Readers"
                      data-testid="bookclub-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="club-desc">Description *</Label>
                    <Textarea
                      id="club-desc"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      required
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="club-genre">Genre *</Label>
                    <Input
                      id="club-genre"
                      value={form.genre}
                      onChange={(e) => setForm({ ...form, genre: e.target.value })}
                      required
                      placeholder="Mystery, Fiction, etc."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="current-book">Currently Reading</Label>
                    <Input
                      id="current-book"
                      value={form.current_book}
                      onChange={(e) => setForm({ ...form, current_book: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Location</Label>
                      <Button
                        type="button"
                        onClick={detectLocation}
                        disabled={detectingLoc}
                        variant="outline"
                        size="sm"
                        className="border-[#6B7A4F] text-[#6B7A4F] text-xs rounded-full"
                      >
                        {detectingLoc ? <Loader2 className="w-3 h-3 animate-spin" /> : <Navigation className="w-3 h-3" />}
                      </Button>
                    </div>
                    <Input
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                    data-testid="bookclub-submit-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Club
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8 max-w-md">
              <TabsTrigger value="all">All Clubs ({allClubs.length})</TabsTrigger>
              <TabsTrigger value="mine">My Clubs ({myClubs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              {loading ? (
                <div className="text-center py-12"><Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" /></div>
              ) : allClubs.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-2xl">
                  <UsersRound className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">No book clubs yet</h3>
                  <p className="text-[#5C5955]">Be the first to create one!</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {allClubs.map((club) => <ClubCard key={club.id} club={club} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="mine">
              {loading ? (
                <div className="text-center py-12"><Loader2 className="w-12 h-12 animate-spin text-[#6B7A4F] mx-auto" /></div>
              ) : myClubs.length === 0 ? (
                <div className="text-center py-12 glass-card rounded-2xl">
                  <UsersRound className="w-16 h-16 text-[#6B7A4F] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-[#2D2A26] mb-2">Not a member of any club yet</h3>
                  <p className="text-[#5C5955]">Browse all clubs or create your own</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {myClubs.map((club) => <ClubCard key={club.id} club={club} showLeave />)}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Meetings Dialog */}
      <Dialog open={meetingsDialogOpen} onOpenChange={setMeetingsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#6B7A4F]" />
              {selectedClub?.name} · Meetings
            </DialogTitle>
            <DialogDescription className="sr-only">Manage details here</DialogDescription>
          </DialogHeader>

          {!meetingFormOpen ? (
            <>
              <div className="flex justify-end">
                <Button
                  onClick={() => setMeetingFormOpen(true)}
                  className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                  data-testid="meeting-schedule-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Schedule Meeting
                </Button>
              </div>

              {meetingsLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-[#6B7A4F] mx-auto" />
                </div>
              ) : meetings.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-[#E1D9CD] rounded-xl">
                  <Coffee className="w-12 h-12 text-[#6B7A4F] mx-auto mb-3 opacity-50" />
                  <p className="text-[#5C5955]">No meetings scheduled yet</p>
                  <p className="text-sm text-[#5C5955] mt-1">Be the first to plan a coffee shop gathering!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {meetings.map((meeting) => {
                    const isPast = new Date(meeting.meeting_date) < new Date();
                    return (
                      <div
                        key={meeting.id}
                        data-testid="meeting-card"
                        className={`border border-[#E1D9CD] rounded-xl p-4 ${isPast ? 'opacity-60' : 'bg-white/50'}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-bold text-[#2D2A26]">{meeting.title}</h4>
                            <p className="text-sm text-[#6B7A4F] font-medium flex items-center gap-1 mt-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDate(meeting.meeting_date)}
                              {isPast && <span className="text-xs text-[#5C5955] ml-2">(past)</span>}
                            </p>
                          </div>
                          {meeting.creator_id === user._id && (
                            <button
                              onClick={() => deleteMeeting(meeting.id)}
                              className="text-red-400 hover:text-red-600 p-1"
                              data-testid="meeting-delete-btn"
                              aria-label="Delete meeting"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div className="flex items-start gap-2 text-sm text-[#5C5955] mb-2">
                          <Coffee className="w-4 h-4 text-[#8E9E82] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-[#2D2A26]">{meeting.venue_name}</p>
                            {meeting.venue_address && (
                              <p className="text-xs">{meeting.venue_address}</p>
                            )}
                          </div>
                        </div>

                        {meeting.description && (
                          <p className="text-sm text-[#5C5955] mb-3 pl-6">{meeting.description}</p>
                        )}

                        <div className="flex items-center justify-between pt-3 border-t border-[#E1D9CD]">
                          <div className="text-xs text-[#5C5955]">
                            <span className="font-semibold text-[#8E9E82]">{meeting.going_count} going</span>
                            {meeting.maybe_count > 0 && (
                              <span> · {meeting.maybe_count} maybe</span>
                            )}
                          </div>
                          {!isPast && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => rsvpMeeting(meeting.id, 'going')}
                                data-testid="meeting-rsvp-going"
                                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                                  meeting.my_rsvp === 'going'
                                    ? 'bg-[#8E9E82] text-white'
                                    : 'bg-[#F4F1EA] text-[#5C5955] hover:bg-[#E2E6D5]'
                                }`}
                              >
                                <Check className="w-3 h-3 inline mr-1" />Going
                              </button>
                              <button
                                onClick={() => rsvpMeeting(meeting.id, 'maybe')}
                                data-testid="meeting-rsvp-maybe"
                                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                                  meeting.my_rsvp === 'maybe'
                                    ? 'bg-[#6B7A4F] text-white'
                                    : 'bg-[#F4F1EA] text-[#5C5955] hover:bg-[#E2E6D5]'
                                }`}
                              >
                                <HelpCircle className="w-3 h-3 inline mr-1" />Maybe
                              </button>
                              <button
                                onClick={() => rsvpMeeting(meeting.id, 'not_going')}
                                data-testid="meeting-rsvp-notgoing"
                                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                                  meeting.my_rsvp === 'not_going'
                                    ? 'bg-red-400 text-white'
                                    : 'bg-[#F4F1EA] text-[#5C5955] hover:bg-red-100'
                                }`}
                              >
                                <X className="w-3 h-3 inline mr-1" />Can't
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <form onSubmit={createMeeting} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="m-title">Title *</Label>
                <Input
                  id="m-title"
                  value={meetingForm.title}
                  onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                  required
                  placeholder="Monthly discussion"
                  data-testid="meeting-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-date">Date & Time *</Label>
                <Input
                  id="m-date"
                  type="datetime-local"
                  value={meetingForm.meeting_date}
                  onChange={(e) => setMeetingForm({ ...meetingForm, meeting_date: e.target.value })}
                  required
                  data-testid="meeting-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-venue">Venue / Coffee Shop *</Label>
                <Input
                  id="m-venue"
                  value={meetingForm.venue_name}
                  onChange={(e) => setMeetingForm({ ...meetingForm, venue_name: e.target.value })}
                  required
                  placeholder="Blue Bottle Coffee, Barnes & Noble..."
                  data-testid="meeting-venue-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-address">Address</Label>
                <Input
                  id="m-address"
                  value={meetingForm.venue_address}
                  onChange={(e) => setMeetingForm({ ...meetingForm, venue_address: e.target.value })}
                  placeholder="123 Main St, City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="m-desc">Notes / Agenda</Label>
                <Textarea
                  id="m-desc"
                  value={meetingForm.description}
                  onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
                  rows={3}
                  placeholder="What will you discuss?"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMeetingFormOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingMeeting}
                  className="flex-1 bg-[#6B7A4F] hover:bg-[#57653F] text-white"
                  data-testid="meeting-submit-btn"
                >
                  {creatingMeeting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Schedule'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Discussions Dialog */}
      <Dialog open={discussionsDialogOpen} onOpenChange={setDiscussionsDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#6B7A4F]" />
              {selectedClub?.name} · Discussions
            </DialogTitle>
            <DialogDescription className="sr-only">Manage details here</DialogDescription>
          </DialogHeader>

          <form onSubmit={createPost} className="space-y-2 pb-4 border-b border-[#E1D9CD]">
            <Textarea
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              placeholder="Start a discussion... What did you think of chapter 5?"
              rows={2}
              className="resize-none"
              data-testid="post-input"
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={posting || !newPostContent.trim()}
                className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
                data-testid="post-submit-btn"
              >
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Post</>}
              </Button>
            </div>
          </form>

          {postsLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#6B7A4F] mx-auto" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-[#E1D9CD] rounded-xl">
              <MessageSquare className="w-12 h-12 text-[#6B7A4F] mx-auto mb-3 opacity-50" />
              <p className="text-[#5C5955]">No posts yet</p>
              <p className="text-sm text-[#5C5955] mt-1">Start the conversation!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  data-testid="post-card"
                  className="bg-white/50 border border-[#E1D9CD] rounded-xl p-4"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-[#2D2A26]">{post.author_name}</p>
                      <p className="text-xs text-[#5C5955]">{formatDate(post.created_at)}</p>
                    </div>
                    {post.author_id === user._id && (
                      <button
                        onClick={() => deletePost(post.id)}
                        className="text-red-400 hover:text-red-600 p-1"
                        data-testid="post-delete-btn"
                        aria-label="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-[#2D2A26] mb-3 whitespace-pre-wrap">{post.content}</p>

                  {/* Comments */}
                  {post.comments && post.comments.length > 0 && (
                    <div className="pl-4 border-l-2 border-[#E2E6D5] space-y-2 mb-3">
                      {post.comments.map((c) => (
                        <div key={c.id} className="text-sm" data-testid="comment-item">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <span className="font-semibold text-[#2D2A26]">{c.author_name}</span>
                              <span className="text-xs text-[#5C5955] ml-2">{formatDate(c.created_at)}</span>
                              <p className="text-[#2D2A26]">{c.content}</p>
                            </div>
                            {c.author_id === user._id && (
                              <button
                                onClick={() => deleteComment(post.id, c.id)}
                                className="text-red-400 hover:text-red-600 p-0.5"
                                aria-label="Delete comment"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Comment input */}
                  <div className="flex gap-2">
                    <Input
                      value={commentInputs[post.id] || ''}
                      onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addComment(post.id); } }}
                      placeholder="Add a comment..."
                      className="flex-1 text-sm"
                      data-testid="comment-input"
                    />
                    <Button
                      type="button"
                      onClick={() => addComment(post.id)}
                      disabled={!(commentInputs[post.id] || '').trim()}
                      size="sm"
                      className="bg-[#8E9E82] hover:bg-[#7A8A72] text-white rounded-full"
                      data-testid="comment-submit-btn"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookClubs;
